import { FunctionComponent, useEffect, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import useAsync from 'react-use/lib/useAsync';
import { configApiRef, useApi } from '@backstage/core-plugin-api';
import { Progress, ResponseErrorPanel } from '@backstage/core-components';
import { isPluginEnabled, StepTimeline } from '@internal/plugin-shared-react';
import { sonarqubeApiRef } from '../../../api/sonarqube-api';
import { SonarQubeServiceGroup } from '../../../api/service-groups/types';
import {
  ExistingSonarQubeProject,
  SonarQubeProjectStep,
  sonarQubeProjectSteps,
  useSonarQubeProjectForm,
} from '../CreateSonarQubeProjectPage/CreateSonarQubeProjectPage';
import shared from '../../../styles.module.css';
import styles from './UpdateSonarQubeProjectPage.module.css';

// Rendered once the project loads, so the form starts out with its current values.
const UpdateFlow = ({
  serviceGroup,
  project,
  onDone,
}: {
  serviceGroup: SonarQubeServiceGroup;
  project: ExistingSonarQubeProject;
  onDone: () => void;
}) => {
  const [activeStep, setActiveStep] = useState(1);
  const form = useSonarQubeProjectForm(serviceGroup, onDone, project);
  
  const isLastStep = activeStep === sonarQubeProjectSteps.length;

  return (
    <StepTimeline
      steps={sonarQubeProjectSteps}
      activeStep={activeStep}
      onCancel={onDone}
      onPrevious={() => setActiveStep(step => Math.max(step - 1, 1))}
      onNext={() =>
        isLastStep
          ? form.submit()
          : setActiveStep(step => step + 1)
      }
      isNextDisabled={!form.isStepValid(activeStep)}
      nextDisabledTooltip={(isLastStep && "Nothing to update") || undefined}
      isSubmitting={form.submitState.loading}
      submitLabel="Update project"
    >
      <SonarQubeProjectStep
        step={activeStep}
        form={form}
        serviceGroup={serviceGroup}
        existing={project}
      />
    </StepTimeline>
  );
};

interface UpdateSonarQubeProjectPageProps {
  /** Called once the service group loads, so the app shell can show its name in the breadcrumb. */
  onServiceGroupLoaded?: (serviceGroup: SonarQubeServiceGroup) => void;
}

/** Route: /product-teams/:id/tooling-groups/sonarqube/:serviceGroupId/projects/:projectKey/edit */
export const UpdateSonarQubeProjectPage: FunctionComponent<
  UpdateSonarQubeProjectPageProps
> = ({ onServiceGroupLoaded }) => {
  const configApi = useApi(configApiRef);
  const sonarqubeApi = useApi(sonarqubeApiRef);
  const navigate = useNavigate();
  const { id = '', serviceGroupId = '', projectKey = '' } = useParams();
  const { value, loading, error } = useAsync(async () => {
    const [serviceGroup, bindings, descriptions, qualityGates] =
      await Promise.all([
        sonarqubeApi.getServiceGroup(serviceGroupId),
        sonarqubeApi.getProjectBindings(serviceGroupId),
        sonarqubeApi.getProjectDescriptions(serviceGroupId),
        sonarqubeApi.getProjectQualityGates(serviceGroupId),
      ]);
    const qualityGate = qualityGates.find(g => g.projectKey === projectKey);
    const project: ExistingSonarQubeProject | undefined = qualityGate && {
      projectKey,
      repositoryName:
        bindings.find(b => b.projectKey === projectKey)?.repository ?? '',
      projectDescription:
        descriptions.find(d => d.projectKey === projectKey)?.description ?? '',
      qualityGateName: qualityGate.name,
    };
    return { serviceGroup, project };
  }, [sonarqubeApi, serviceGroupId, projectKey]);

  useEffect(() => {
    if (value?.serviceGroup) {
      onServiceGroupLoaded?.(value.serviceGroup);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value?.serviceGroup]);

  if (!isPluginEnabled(configApi, 'sonarqube')) {
    return <Navigate to="/" replace />;
  }
  if (loading) {
    return <Progress />;
  }
  if (error) {
    return <ResponseErrorPanel error={error} />;
  }

  return (
    <div className={styles.body}>
      {value?.serviceGroup && value.project ? (
        <UpdateFlow
          serviceGroup={value.serviceGroup}
          project={value.project}
          onDone={() =>
            navigate(
              `/product-teams/${encodeURIComponent(
                id,
              )}/tooling-groups/sonarqube/${encodeURIComponent(serviceGroupId)}`,
            )
          }
        />
      ) : (
        <span className={shared.hintText}>
          {value?.serviceGroup
            ? 'Project not found.'
            : 'Tooling group not found.'}
        </span>
      )}
    </div>
  );
};
